import React from 'react';

import { MaterialPropertyValuePair } from '@paragon/client-tools';
import { Member } from '@paragon/contract-library';
import { angleEquals, convertDegreesToRadians } from '@paragon/geometry-library';
import { hasPricingPermission as hasPricingPermissionSelector } from '@paragon/login-tools';
import { calculateMemberOverallLength } from '@paragon/three-shared';

import { RootState } from '../../../../../store';
import { getActiveAnalysisSet } from '../../../../../store/selectors/getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from '../../../../../store/selectors/getActiveComponentDesignSelector';
import { getDistanceFormatterSelector } from '../../../../../store/selectors/getDistanceFormatterSelector';
import { getMaxCsiForSingleSelectedItem } from '../../../../../store/selectors/getMaxCsiOrJsiForSelectedItemSelector';
import { getSingleSelectedItemEntryOrNull } from '../../../../../store/selectors/getSelectedItemSelector';
import { createGetMemberPrice } from '../../../../../store/selectors/stockLengthAndPriceSelectors';
import { useAppSelector } from '../../../../hooks';

// no `member` prop; a single selected member is assumed / required by old components used here
export function SingleMemberAdvancedProperties() {
  const member = useAppSelector(getSelectedItemAsMember);
  const componentDesign = useAppSelector(getActiveComponentDesign);

  const hasPricingPermission = useAppSelector(hasPricingPermissionSelector);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const bracingCount = useAppSelector(getBracingCount);
  const maxCsi = useAppSelector(getMaxCsiForSingleSelectedItem);
  const lumberPrice = useAppSelector(getLumberPrice);

  if (member == null || componentDesign == null) return null;

  return (
    <>
      <MaterialPropertyValuePair
        sx={{ m: 1 }}
        property="Length"
        value={
          calculateMemberOverallLength(member, componentDesign!.component)
            ? distanceFormatter(calculateMemberOverallLength(member, componentDesign!.component)!)
            : 'Unknown'
        }
        key="length"
      />
      <MaterialPropertyValuePair sx={{ m: 1 }} property="Permanent Bracing" value={bracingCount} key="bracing" />
      <MaterialPropertyValuePair sx={{ m: 1 }} property="Pitch" value={getPitchString(member)} key="pitch" />
      <MaterialPropertyValuePair
        sx={{ m: 1 }}
        property="Angle With Horizontal"
        value={`${member.angle.toFixed(2)}°`}
        key="angle"
      />
      <MaterialPropertyValuePair
        sx={{ m: 1 }}
        property="Cross Sectional Area"
        value={`${member.crossSectionalArea.toString()} square inches`}
        key="cross-section"
      />

      {maxCsi && <MaterialPropertyValuePair sx={{ m: 1 }} property="Max Csi" value={maxCsi} key="maxCsi" />}

      {hasPricingPermission && (
        <MaterialPropertyValuePair sx={{ m: 1 }} property="Member Price" value={lumberPrice} key="price" />
      )}
    </>
  );
}

function getSelectedItemAsMember(state: RootState) {
  return getSingleSelectedItemEntryOrNull(state)!.item as Member;
}

function getPitchString(member: Member) {
  if (angleEquals(member.angle, 90)) return 'Vertical';
  const slope = Math.tan(convertDegreesToRadians(member.angle));
  return `${(slope * 12).toFixed(3)} in. rise per ft`;
}

function getBracingCount(state: RootState) {
  const activeAnalysisSet = getActiveAnalysisSet(state);
  const member = getSelectedItemAsMember(state);
  return activeAnalysisSet && activeAnalysisSet.bracing[member.guid]
    ? activeAnalysisSet.bracing[member.guid].length
    : 0;
}

function getLumberPrice(state: RootState) {
  const componentDesign = getActiveComponentDesign(state);
  const member = getSelectedItemAsMember(state);
  const getMemberPrice = createGetMemberPrice(state);
  const lumberPrice = componentDesign && getMemberPrice(componentDesign, member);
  return lumberPrice ? `$${lumberPrice.toFixed(2)}` : 'Not available';
}
